import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { httpOk, GET } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, formatPeriodLabel } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "14px",
	boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
}

const mutedStyle = { color: "var(--dark-text-muted, #8791a1)", fontSize: "12px" }
const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

const severityMeta = {
	danger: { color: "#dc2626", bg: "var(--dark-tint-danger, #fdecec)", icon: "uil-exclamation-triangle" },
	warning: { color: "#e0762c", bg: "var(--dark-tint-warning, #fff4df)", icon: "uil-exclamation-circle" },
	success: { color: "#16a34a", bg: "var(--dark-tint-success, #e7f8ef)", icon: "uil-check-circle" },
	info: { color: "#2563eb", bg: "var(--dark-tint-primary, #eaf2ff)", icon: "uil-info-circle" },
}

const branchStatusMeta = {
	new: { label: "ai_status_new", color: "#2563eb", bg: "var(--dark-tint-primary, #eaf2ff)" },
	no_sales: { label: "ai_status_no_sales", color: "#dc2626", bg: "var(--dark-tint-danger, #fdecec)" },
}

function formatDateTime(value) {
	if (!value) return ""
	return formatDate(String(value).replace(" ", "T"), "dd.MM.yyyy HH:mm")
}

// Рост от нулевой базы бэкенд отдаёт null: это «новое», а не +100%
function Growth({ percent, current }) {
	const { t } = useTranslation()
	if (percent === null || percent === undefined) {
		if (Number(current || 0) > 0) return <span style={{ color: "var(--dark-fg-primary, #2563eb)", fontWeight: 800 }}>{t("ai_growth_new")}</span>
		return <span style={mutedStyle}>—</span>
	}
	const number = Number(percent)
	const color = number > 0 ? "#16a34a" : number < 0 ? "#dc2626" : "#8791a1"
	return <span style={{ color, fontWeight: 800 }}>{number > 0 ? "+" : ""}{formatMoney(number, 1)}%</span>
}

// Плашка о состоянии AI: модель не ответила (нет ключа, таймаут, мусор) — показаны правила
function AiStatus({ data }) {
	const { t } = useTranslation()
	if (!data || (!data.ai_fallback && data.ai_enabled !== false)) return null
	const reason = data.ai_enabled === false ? t("ai_not_connected") : t("ai_unavailable_now")
	return (
		<div className="alert alert-warning d-flex align-items-center gap-2 mb-3" style={{ borderRadius: "12px" }}>
			<i className="uil uil-info-circle"></i>
			<span>{t("ai_fallback_notice", { reason })}</span>
		</div>
	)
}

function InsightCard({ item }) {
	const { t } = useTranslation()
	const meta = severityMeta[item.severity] || severityMeta.info
	return (
		<div className="mb-3" style={{ ...cardStyle, background: meta.bg, borderColor: `${meta.color}30`, padding: "16px" }}>
			<div className="d-flex align-items-start gap-3">
				<i className={`uil ${meta.icon}`} style={{ color: meta.color, fontSize: "20px", marginTop: "2px" }}></i>
				<div className="flex-grow-1">
					<div className="d-flex align-items-center gap-2 flex-wrap">
						<h5 className="mb-0" style={{ fontWeight: 900 }}>{item.title}</h5>
						{item.status && <span style={{ background: "var(--dark-surface, #fff)", borderRadius: "12px", padding: "3px 8px", color: meta.color, fontSize: "12px", fontWeight: 800 }}>{item.status}</span>}
						{item.metric && <span style={{ ...mutedStyle, color: meta.color }}>{item.metric}</span>}
					</div>
					<div className="mt-2" style={{ color: "var(--dark-text, #374151)" }}>{item.summary}</div>
					{item.recommendation && (
						<div className="mt-3" style={{ background: "var(--dark-hover, rgba(255,255,255,0.65))", borderRadius: "12px", padding: "10px 12px" }}>
							<div style={{ ...mutedStyle, textTransform: "uppercase", fontWeight: 800 }}>{t("ai_recommendation")}</div>
							<div>{item.recommendation}</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

function Metric({ label, value, growth, current }) {
	return (
		<div>
			<div style={mutedStyle}>{label}</div>
			<b>{value}</b>
			{growth !== false && <div style={{ fontSize: "12px" }}><Growth percent={growth} current={current} /></div>}
		</div>
	)
}

function BranchesTable({ branches }) {
	const { t } = useTranslation()
	if (branches.length === 0) return null
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-2" style={{ fontWeight: 900 }}>{t("ai_branches")}</h5>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>{t("ai_col_branch")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("ai_revenue")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("ai_cheques")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("ai_previous_period")}</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>{t("ai_growth")}</th>
							</tr>
						</thead>
						<tbody>
							{branches.map(branch => {
								const status = branchStatusMeta[branch.status]
								return (
									<tr key={branch.pos_id}>
										<td className="py-3" style={{ fontWeight: 800 }}>
											{branch.name}
											{status && <span className="ms-2" style={{ background: status.bg, color: status.color, borderRadius: "10px", padding: "2px 8px", fontSize: "12px", fontWeight: 800 }}>{t(status.label)}</span>}
										</td>
										<td className="py-3 text-end" style={{ fontWeight: 800 }}>{branch.revenue_short}</td>
										<td className="py-3 text-end">{formatMoney(branch.cheques, 0)}</td>
										<td className="py-3 text-end text-muted">{formatMoney(branch.previous_revenue, 0)}</td>
										<td className="py-3 text-end"><Growth percent={branch.revenue_growth_percent} current={branch.revenue} /></td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)

	const filters = useAnalyticsFilters({
		...buildPeriod("7days"),
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
	}, searchWithFilter)
	const options = useAnalyticsOptions(["sources"], filters.draft.pos_id)

	// Первый запрос долгий (модель думает до ~25 с), дальше ответ берётся из кэша
	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/ai-insights", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const insights = data?.insights || []
	const current = data?.metrics?.current || {}
	const growth = data?.metrics?.growth || {}
	const branches = data?.metrics?.branches || []
	const comparePeriod = formatPeriodLabel(data?.period?.compare_start_date, data?.period?.compare_end_date)

	return (
		<>
			<AnalyticsFilterBar
				title={t("ai_insights_title")}
				icon="uil-lightbulb-alt"
				subtitle={t("ai_insights_subtitle")}
				filters={filters}
			>
				<StandardFields filters={filters} options={options} />
			</AnalyticsFilterBar>

			<AiStatus data={data} />

			<div className="card mb-3" style={cardStyle}>
				<div className="card-body">
					<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
						<div style={{ flex: "1 1 320px" }}>
							<h5 className="mb-1" style={{ fontWeight: 900 }}>{t("ai_period_summary")}</h5>
							<div style={{ color: "var(--dark-text, #374151)" }}>{data?.summary || t("ai_no_data")}</div>
							{data?.generated_at && (
								<div className="mt-2" style={mutedStyle}>
									{t("ai_generated_at", { date: formatDateTime(data.generated_at) })}
									{data.ai_cached && ` · ${t("ai_cached_note")}`}
								</div>
							)}
						</div>
						<div className="d-flex gap-4 flex-wrap">
							<Metric label={t("ai_revenue")} value={current.revenue_short || "0"} growth={growth.revenue_percent} current={current.revenue} />
							<Metric label={t("ai_cheques")} value={formatMoney(current.cheques, 0)} growth={growth.cheques_percent} current={current.cheques} />
							<Metric label={t("ai_average_cheque")} value={current.average_cheque_short || "0"} growth={growth.average_cheque_percent} current={current.average_cheque} />
							{Number(current.returns || 0) > 0 && <Metric label={t("ai_returns")} value={formatMoney(current.returns, 0)} growth={false} />}
						</div>
					</div>
					<div className="mt-2" style={mutedStyle}>
						{t("ai_revenue_net_note")}{comparePeriod && `; ${t("ai_growth_to_period", { period: comparePeriod })}`}
					</div>
				</div>
			</div>

			{insights.map((item, index) => <InsightCard item={item} key={`${item.title}-${index}`} />)}

			<BranchesTable branches={branches} />
		</>
	)
}

export default Index
